/**
 * @fileoverview Main view component that handles Zoom SDK initialization
 * and renders the unified dashboard view for all participants.
 *
 * All participants can view the moderation dashboard. Host-only controls
 * are conditionally rendered based on user role.
 *
 * @module components/MainView
 */

import React, { useEffect, useRef, useState } from 'react';
import { useMeetingState } from '../context/MeetingStateContext';
import HostView from './HostView';
// import ScreenShareAgendaView from './ScreenShareAgendaView'; // v1 — kept for reference
import RithmoStageView from './RithmoStageView';

/**
 * Main view component that orchestrates the application initialization.
 *
 * This component:
 * 1. Waits for the Zoom SDK to be available
 * 2. Initializes the Zoom App context
 * 3. Renders the unified dashboard view for all participants
 *    (host controls are shown/hidden based on user role within HostView)
 *
 * @returns {JSX.Element} The dashboard view with role-based controls
 */
function MainView() {
  const { isInitialized, error, initZoomApp, startDemoMode } = useMeetingState();
  const initAttemptedRef = useRef(false);
  const initIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [initStatus, setInitStatus] = useState('Checking Zoom SDK...');
  const [sdkAvailable, setSdkAvailable] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const [initTimeout, setInitTimeout] = useState(false);

  useEffect(() => {
    // Only attempt initialization once
    if (initAttemptedRef.current) {
      return;
    }

    // Playwright screen-share capture: load dashboard via ?meetingId=&shareCapture=1 without
    // waiting for the Zoom SDK (SDK may exist in Chromium but is not usable outside Zoom).
    const params = new URLSearchParams(window.location.search);
    if (params.get('shareCapture') === '1' && params.get('meetingId')) {
      initAttemptedRef.current = true;
      setInitStatus('Headless capture — initializing meeting state...');
      initZoomApp();
      return;
    }

    // Check if Zoom SDK is ready
    const checkAndInit = () => {
      if (initAttemptedRef.current) {
        return true; // Already attempted, stop checking
      }

      if (window.zoomSdk && typeof window.zoomSdk.config === 'function') {
        initAttemptedRef.current = true;
        setSdkAvailable(true);
        setInitStatus('Zoom SDK found, initializing...');
        console.log('[MainView] Zoom SDK found, calling initZoomApp...');
        initZoomApp();
        return true;
      }
      return false;
    };

    if (!checkAndInit()) {
      let attempts = 0;
      initIntervalRef.current = setInterval(() => {
        attempts++;
        setInitStatus(`Waiting for Zoom SDK... (${attempts}/30)`);
        if (checkAndInit()) {
          if (initIntervalRef.current !== null) {
            clearInterval(initIntervalRef.current);
            initIntervalRef.current = null;
          }
        } else if (attempts >= 30) {
          if (initIntervalRef.current !== null) {
            clearInterval(initIntervalRef.current);
            initIntervalRef.current = null;
          }
          initAttemptedRef.current = true;
          setInitStatus('Zoom SDK not available. Running in demo mode.');
          setInitTimeout(true);
          // Force show the app in demo mode after timeout
          setForceShow(true);
        }
      }, 300);
    }

    return () => {
      if (initIntervalRef.current !== null) {
        clearInterval(initIntervalRef.current);
        initIntervalRef.current = null;
      }
    };
  }, [initZoomApp]);

  // Timeout to force show the app if initialization takes too long
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isInitialized && !forceShow) {
        console.log('[MainView] Initialization timeout, forcing show...');
        setForceShow(true);
        setInitStatus('Initialization timeout - showing app anyway');
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [isInitialized, forceShow]);

  // Show error with option to continue in demo mode
  if (error && !forceShow) {
    return (
      <div className="empty-state">
        <div className="empty-icon">⚠️</div>
        <p style={{ color: '#ff6b6b', fontWeight: 'bold' }}>Initialization Issue</p>
        <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>{error}</p>
        <p style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
          This is expected when running outside Zoom. You can continue in demo mode.
        </p>
        <button
          onClick={() => {
            setForceShow(true);
            startDemoMode();
          }}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #00b4d8 0%, #0077b6 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600'
          }}
        >
          🚀 Continue in Demo Mode
        </button>
      </div>
    );
  }

  // Show loading state only if not initialized AND not forced to show
  if (!isInitialized && !forceShow) {
    return (
      <div className="empty-state">
        <div className="empty-icon">⏳</div>
        <p>Initializing Rithmo AI...</p>
        <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>{initStatus}</p>
        {sdkAvailable && <p style={{ fontSize: '11px', color: '#666' }}>SDK detected, configuring...</p>}
        <button
          onClick={() => setForceShow(true)}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: '#4a90d9',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Skip & Show App
        </button>
      </div>
    );
  }

  // Show warning banner if running without proper initialization
  const showWarning = forceShow && !isInitialized;

  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isScreenshareAgendaView =
    searchParams.get('shareCapture') === '1' &&
    Boolean(searchParams.get('meetingId')) &&
    searchParams.get('shareView') === 'screenshare';

  // Always show the unified dashboard view for all participants
  // Host-only controls are handled within HostView based on state.isHost
  return (
    <>
      {showWarning && (
        <div style={{
          background: '#ff9800',
          color: '#000',
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          ⚠️ Demo Mode - Zoom SDK not initialized. Some features may not work.
        </div>
      )}
      {error && (
        <div style={{
          background: '#f44336',
          color: '#fff',
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '12px'
        }}>
          Error: {error}
        </div>
      )}
      {isScreenshareAgendaView ? <RithmoStageView /> : <HostView />}
    </>
  );
}

export default MainView;
